import { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from '../AuthContext';
import { useLanguage } from '../LanguageContext';
import translations from '../translations';
import './CollectionsPage.css';

function CollectionsPage({ onSelectCollection, onBack }) {
  const { language } = useLanguage();
  const t = translations[language];
  const { user, isAdmin, isMember } = useAuth();

  const [collections, setCollections] = useState([]);
  const [counts, setCounts] = useState({});   // collection_id → count
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // collection id

  useEffect(() => {
    fetchCollections();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, isMember, user?.id]);

  async function fetchCollections() {
    // Each user sees only their own collections:
    // admin → user_id IS NULL (Szilvia's pre-existing collections)
    // member → user_id = their uuid
    let query = supabase.from('collections').select('*').order('created_at', { ascending: true });
    if (isAdmin) query = query.is('user_id', null);
    else if (isMember) query = query.eq('user_id', user.id);
    const { data: cols } = await query;

    if (cols) {
      setCollections(cols);
      // Fetch recipe counts for each collection
      const { data: links } = await supabase
        .from('collection_recipes')
        .select('collection_id');
      if (links) {
        const c = {};
        for (const link of links) {
          c[link.collection_id] = (c[link.collection_id] || 0) + 1;
        }
        setCounts(c);
      }
    }
    setLoading(false);
  }

  async function handleCreate(e) {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setCreating(true);

    // Auto-translate name to Hungarian
    let name_hu = null;
    try {
      const res = await fetch('/.netlify/functions/translate-text', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: name, targetLanguage: 'hu' }),
      });
      const json = await res.json();
      if (json.translated) name_hu = json.translated;
    } catch (_) {}

    const { data, error } = await supabase.from('collections').insert({
      name,
      name_hu,
      user_id: isMember ? user.id : null,
    }).select().single();
    if (!error && data) {
      setCollections(prev => [...prev, data]);
      setCounts(prev => ({ ...prev, [data.id]: 0 }));
    }
    setNewName('');
    setCreating(false);
    setShowCreate(false);
  }

  async function handleDelete(id) {
    await supabase.from('collections').delete().eq('id', id);
    setCollections(prev => prev.filter(c => c.id !== id));
    setConfirmDelete(null);
  }

  return (
    <main className="collections-page">
      <div className="collections-page__header">
        <button className="collections-page__back" onClick={onBack}>← {t.allCategories}</button>
        <div className="collections-page__title-row">
          <h2 className="collections-page__title">{t.collectionsTitle}</h2>
          {(isAdmin || isMember) && (
            <button className="collections-page__new-btn" onClick={() => setShowCreate(v => !v)}>
              {t.newCollection}
            </button>
          )}
        </div>
        {showCreate && (isAdmin || isMember) && (
          <form className="collections-page__create-form" onSubmit={handleCreate}>
            <input
              className="collections-page__create-input"
              type="text"
              placeholder={t.collectionNamePlaceholder}
              value={newName}
              onChange={e => setNewName(e.target.value)}
              autoFocus
            />
            <button className="collections-page__create-submit" type="submit" disabled={creating || !newName.trim()}>
              {t.createCollection}
            </button>
          </form>
        )}
      </div>

      {loading ? (
        <p style={{ color: '#999', fontStyle: 'italic' }}>{t.loading}</p>
      ) : collections.length === 0 ? (
        <p className="collections-page__empty">{t.collectionsEmpty}</p>
      ) : (
        <div className="collections-page__grid">
          {collections.map(col => (
            <div key={col.id} className="collection-tile" onClick={() => onSelectCollection(col)}>
              <div className="collection-tile__icon">📁</div>
              <div className="collection-tile__body">
                <span className="collection-tile__name">{(language === 'hu' && col.name_hu) ? col.name_hu : col.name}</span>
                <span className="collection-tile__count">
                  {(counts[col.id] || 0) === 1 ? t.oneRecipe : t.nRecipes(counts[col.id] || 0)}
                </span>
              </div>
              {(isAdmin || (isMember && col.user_id === user?.id)) && (
                <button
                  className="collection-tile__delete"
                  onClick={e => { e.stopPropagation(); setConfirmDelete(col.id); }}
                  aria-label="Delete collection"
                >✕</button>
              )}
              {confirmDelete === col.id && (
                <div className="collection-tile__confirm" onClick={e => e.stopPropagation()}>
                  <span>{t.deleteCollectionConfirm}</span>
                  <button className="collection-tile__confirm-yes" onClick={() => handleDelete(col.id)}>{t.deleteCollection}</button>
                  <button className="collection-tile__confirm-no" onClick={() => setConfirmDelete(null)}>{t.cancel}</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </main>
  );
}

export default CollectionsPage;
